import { isDesktop } from './utils';

// from <720px to >720px
export const MOBILE_TO_TABLET = 'pesMobileToTablet';
// from >720px to <720px
export const TABLET_TO_MOBILE = 'pesTabletToMobile';
// from <980px to >980px
export const TABLET_TO_DESKTOP = 'pesTabletToDesktop';
// from >980px to <980px
export const DESKTOP_TO_TABLET = 'pesDesktopToTablet';
// from <1200px to >1200px
export const DESKTOP_TO_WIDE_DESKTOP = 'pesDesktopToWideDesktop';
// from >1200px to <1200px
export const WIDE_DESKTOP_TO_DESKTOP = 'pesWideDesktopToDesktop';

/**
 * Desktop Ranges.
 *
 * @typedef {object} DesktopRange
 * @property {boolean} [isMobile] Is screen range Mobile.
 * @property {boolean} [isTablet] Is screen range Tablet.
 * @property {boolean} [isDesktop] Is screen range Desktop.
 * @property {boolean} [isWideDesktop] Is screen range Wide Desktop.
 */

/**
 * @type {DesktopRange}
 * @default
 */
const DESKTOP_RANGE = {
  isMobile: false,
  isTablet: false,
  isDesktop: false,
  isWideDesktop: false
};

/**
 * Called when screen width changes from mobile to tablet.
 *
 * @param {() => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onMobileToTablet(callback, options) {
  window.addEventListener(MOBILE_TO_TABLET, callback, options);

  return function removeEventListener() {
    window.removeEventListener(MOBILE_TO_TABLET, callback, options);
  };
}

/**
 * Called when screen width changes from tablet to mobile.
 *
 * @param {() => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onTabletToMobile(callback, options) {
  window.addEventListener(TABLET_TO_MOBILE, callback, options);

  return function removeEventListener() {
    window.removeEventListener(TABLET_TO_MOBILE, callback, options);
  };
}

/**
 * Called when screen width changes from tablet to desktop. Use instead of  `$(window).on('mobileToDesktop')`.
 *
 * @param {() => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onTabletToDesktop(callback, options) {
  window.addEventListener(TABLET_TO_DESKTOP, callback, options);

  return function removeEventListener() {
    window.removeEventListener(TABLET_TO_DESKTOP, callback, options);
  };
}

/**
 * Called when screen width changes from desktop to tablet. . Use instead of  `$(window).on('desktopToMobile')`.
 *
 * @param {() => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onDesktopToTablet(callback, options) {
  window.addEventListener(DESKTOP_TO_TABLET, callback, options);

  return function removeEventListener() {
    window.removeEventListener(DESKTOP_TO_TABLET, callback, options);
  };
}

/**
 * Called when screen width changes from desktop to wide desktop.
 *
 * @param {() => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onDesktopToWideDesktop(callback, options) {
  window.addEventListener(DESKTOP_TO_WIDE_DESKTOP, callback, options);

  return function removeEventListener() {
    window.removeEventListener(DESKTOP_TO_WIDE_DESKTOP, callback, options);
  };
}

/**
 * Called when screen width changes from wide desktop to desktop.
 *
 * @param {() => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onWideDesktopToDesktop(callback, options) {
  window.addEventListener(WIDE_DESKTOP_TO_DESKTOP, callback, options);

  return function removeEventListener() {
    window.removeEventListener(WIDE_DESKTOP_TO_DESKTOP, callback, options);
  };
}

/**
 * Called when screen width changes from mobile to tablet and vice versa.
 *
 * @param {(desktopRange: DesktopRange) => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onTabletMediaChange(callback, options) {
  const onSmallToLargeCallback = () => callback({ ...DESKTOP_RANGE, isMobile: false, isTablet: true });
  const onLargeToSmallCallback = () => callback({ ...DESKTOP_RANGE, isMobile: true, isTablet: false });

  const removeOnMobileToTabletEventListener = onMobileToTablet(onSmallToLargeCallback, options);
  const removeOnTabletToMobileEventListener = onTabletToMobile(onLargeToSmallCallback, options);

  return function removeEventListeners() {
    removeOnMobileToTabletEventListener();
    removeOnTabletToMobileEventListener();
  };
}

/**
 * Called when screen width changes from desktop to mobile and vice versa.
 *
 * @param {(desktopRange: DesktopRange) => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onDesktopMediaChange(callback, options) {
  const onSmallToLargeCallback = () => callback({ ...DESKTOP_RANGE, isTablet: false, isDesktop: true });
  const onLargeToSmallCallback = () => callback({ ...DESKTOP_RANGE, isTablet: true, isDesktop: false });

  const removeOnTabletToDesktopEventListener = onTabletToDesktop(onSmallToLargeCallback, options);
  const removeOnDesktopToTabletEventListener = onDesktopToTablet(onLargeToSmallCallback, options);

  return function removeEventListeners() {
    removeOnTabletToDesktopEventListener();
    removeOnDesktopToTabletEventListener();
  };
}

/**
 * Called when screen width changes from wide desktop to desktop and vice versa.
 *
 * @param {(desktopRange: DesktopRange) => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onWideDesktopMediaChange(callback, options) {
  const onSmallToLargeCallback = () => callback({ ...DESKTOP_RANGE, isDesktop: false, isWideDesktop: true });
  const onLargeToSmallCallback = () => callback({ ...DESKTOP_RANGE, isDesktop: true, isWideDesktop: false });

  const removeOnDesktopToWideDesktopEventListener = onDesktopToWideDesktop(onSmallToLargeCallback, options);
  const removeOnWideDesktopToDesktopEventListener = onWideDesktopToDesktop(onLargeToSmallCallback, options);

  return function removeEventListener() {
    removeOnDesktopToWideDesktopEventListener();
    removeOnWideDesktopToDesktopEventListener();
  };
}

/**
 * Called when PES screen ranges are changed.
 *
 * @param {(desktopRange: DesktopRange) => void} callback
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void} Remove event listener function.
 */
export function onMediaChange(callback, options) {
  const removeOnTabletMediaChangeListener = onTabletMediaChange(callback, options);
  const removeOnDesktopMediaChangeListener = onDesktopMediaChange(callback, options);
  const removeOnWideDesktopMediaChangeListener = onWideDesktopMediaChange(callback, options);

  return function removeEventListeners() {
    removeOnTabletMediaChangeListener();
    removeOnDesktopMediaChangeListener();
    removeOnWideDesktopMediaChangeListener();
  };
}

/**
 * Calls media specific callback and registers media change listeners.
 *
 * @param {object} params
 * @param {() => void} params.onDesktopToTablet
 * @param {() => void} params.onTabletToDesktop
 * @returns {() => void} Remove event listeners function.
 */
export function initForMedia(params) {
  if (isDesktop()) {
    params.onTabletToDesktop();
  } else {
    params.onDesktopToTablet();
  }

  const removeOnTabletToDesktopListener = onTabletToDesktop(params.onTabletToDesktop);
  const removeOnDesktopToTabletListener = onDesktopToTablet(params.onDesktopToTablet);

  return function removeEventListeners() {
    removeOnTabletToDesktopListener();
    removeOnDesktopToTabletListener();
  };
}
